<!-- MOLECULE: FooterBrand.vue -->

<script setup lang="ts">
//
// ============================================================================
// TYPES
// ============================================================================
//

interface DescriptionLink {
  keyword: string;
  url: string;
  tooltip?: string;
}

interface DescriptionPart {
  text: string;
  isLink: boolean;
  url?: string;
  tooltip?: string;
}

//
// ============================================================================
// PROPS
// ============================================================================
//

interface Props {
  logoSrc?: string | null;
  logoAlt?: string;
  logoSize?: number;
  title?: string | null;
  subtitle?: string;
  description?: string;
  disclaimer?: string;
  descriptionLinks?: DescriptionLink[];
}

const props = withDefaults(defineProps<Props>(), {
  logoSrc: null,
  logoAlt: "Laboratory Logo",
  logoSize: 36,
  title: null,
  subtitle: "Laboratory Information System",
  description: "Modern laboratory information system for managing samples and results.",
  disclaimer: "All rights reserved.",
  descriptionLinks: () => [],
});

//
// ============================================================================
// COMPUTED PROPERTIES
// ============================================================================
//

/**
 * Parse description text and convert specified keywords into links
 *
 * Purpose:
 * - Split description into text and link parts
 * - Match keywords from descriptionLinks prop
 * - Create structured array for template rendering
 *
 * Responsibilities:
 * - Handle multiple keyword matches
 * - Preserve text order
 * - Support overlapping matches (first match wins)
 * - Handle empty description or no links
 */
const descriptionParts = computed<DescriptionPart[]>(() => {
  if (!props.description) return [];
  if (!props.descriptionLinks || props.descriptionLinks.length === 0) {
    return [{ text: props.description, isLink: false }];
  }

  const parts: DescriptionPart[] = [];
  const remainingText = props.description;
  let searchStartIndex = 0;

  while (searchStartIndex < remainingText.length) {
    // Find the earliest keyword match in remaining text
    let earliestMatch: {
      index: number;
      keyword: string;
      link: DescriptionLink;
    } | null = null;

    for (const link of props.descriptionLinks) {
      const index = remainingText
        .toLowerCase()
        .indexOf(link.keyword.toLowerCase(), searchStartIndex);
      if (index !== -1 && (earliestMatch === null || index < earliestMatch.index)) {
        earliestMatch = { index, keyword: link.keyword, link };
      }
    }

    // No more matches found
    if (!earliestMatch) {
      if (searchStartIndex < remainingText.length) {
        parts.push({
          text: remainingText.substring(searchStartIndex),
          isLink: false,
        });
      }
      break;
    }

    // Add text before match
    if (earliestMatch.index > searchStartIndex) {
      parts.push({
        text: remainingText.substring(searchStartIndex, earliestMatch.index),
        isLink: false,
      });
    }

    // Add matched keyword as link
    const matchedText = remainingText.substr(earliestMatch.index, earliestMatch.keyword.length);
    parts.push({
      text: matchedText,
      isLink: true,
      url: earliestMatch.link.url,
      tooltip: earliestMatch.link.tooltip,
    });

    // Move search position past this match
    searchStartIndex = earliestMatch.index + earliestMatch.keyword.length;
  }

  return parts;
});
</script>

<template>
  <div>
    <!-- Laboratory Info -->
    <div class="d-flex align-center ga-2 mb-4">
      <!-- Logo Image -->
      <v-img
        v-if="logoSrc"
        :src="logoSrc"
        :lazy-src="logoSrc"
        :alt="logoAlt"
        :width="logoSize"
        height="auto"
        class="d-flex flex-0-1"
      >
        <!-- Loading -->
        <template #placeholder>
          <div class="d-flex align-center justify-center fill-height">
            <v-progress-circular indeterminate color="primary" size="24" />
          </div>
        </template>

        <!-- Error fallback -->
        <template #error>
          <div class="d-flex align-center justify-center fill-height bg-grey-lighten-3">
            <v-icon icon="mdi-image-broken" size="24" color="grey" />
          </div>
        </template>
      </v-img>

      <!-- Lab Name & Short Description -->
      <div>
        <h3 class="text-h6 font-weight-medium">{{ title || "Laboratory" }}</h3>
        <p class="text-caption text-medium-emphasis mt-n1">
          {{ subtitle }}
        </p>
      </div>
    </div>

    <!-- Long Description -->
    <p class="text-body-2">
      <template v-for="(part, index) in descriptionParts" :key="index">
        <!-- Link -->
        <v-hover v-if="part.isLink" v-slot="{ isHovering, props: hoverProps }">
          <a
            v-tooltip:top="{
              text: part.tooltip || '',
              openDelay: 500,
              closeDelay: 200,
            }"
            v-bind="hoverProps"
            :href="part.url"
            target="_blank"
            class="text-primary text-decoration-none font-weight-medium"
            :class="[isHovering ? 'opacity-80 text-decoration-underline' : 'opacity-100']"
            :text="part.text"
          />
        </v-hover>

        <!-- Regular Text -->
        <span v-else>{{ part.text }}</span>
      </template>
    </p>

    <p class="text-body-2 mt-1">
      {{ disclaimer }}
    </p>
  </div>
</template>
